import {APP_INITIALIZER, NgModule, Optional, SkipSelf} from "@angular/core";
import {
    AuthModule,
    CombinedTranslateLoader,
    ConfigurationModule,
    ConfigurationService,
    DataClientVersion,
    ErrorModule,
    FedeltaHttpModule,
    I18nModule,
    I18nService,
    ImageService,
    ResourceModule,
    ValidationModule,
    VersionService
} from "@fedeltapointofsale/data-client";
import {AuthGuardService} from "../auth-guard.service";
import {ErrorHandlerService} from "../error-handler.service";
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {SessionService} from "../session.service";
import {FUIAlertService, FUINavigationLoader, UiModule} from "@fedeltapointofsale/ui";
import {CanDeactivateEditPage} from "../framework/entity/edit/edit-page/can-deactivate-edit-page";
import {WBOVersion} from "../version";
import {CustomerSupportMessagingService} from "../customer-support-messaging/customer-support-messaging.service";
import {AnalyticsService} from "../analytics/analytics.service";
import {CanDeactivateStackedModal} from "../framework/entity/edit/edit-page/can-deactivate-stacked-modal";
import {NavigationLoader} from "../main/navigation-loader";
import {
    CustomReportConfigSearchModel
} from "../reports/custom-reports/custom-report-config/custom-report-config-search.model";
import {Local, WhoAmI} from "../config";
import {UserProfileSummaryEntityModel} from "../user-profile/user-profile-summary-entity.model";
import {first, map, Observable} from "rxjs";
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {DragDropConfig} from "../shared/dnd/dnd.config";
import {
    DragDropService,
    dragDropServiceFactory,
    DragDropSortableService,
    dragDropSortableServiceFactory
} from "../shared/dnd/dnd.service";
import {ZXingScannerModule} from "@zxing/ngx-scanner";

export class WboTranslateLoader extends CombinedTranslateLoader {
    override getTranslation(language: string): Observable<any> {
        return super.getTranslation(language).pipe(
            map(translation => {
                this.setTranslationKey(translation, "wbo.common.system.this", "\{\{wbo.common.system.fedelta\}\}");
                return translation;
            })
        );
    }

    setTranslationKey(translation: any, key: string, value: string): void {
        const keys = key.split(".");
        const root = keys.slice(0, keys.length - 1).reduce((entity, fieldName) => entity ? entity[fieldName] : undefined, translation);
        if (root) {
            root[keys[keys.length - 1]] = value;
        }
    }
}

export function translateLoaderFactory(i18nService: I18nService, httpClient: HttpClient) {
    return new WboTranslateLoader(i18nService, httpClient, "assets/i18n/");
}

export function navigationLoaderFactory(httpClient: HttpClient, customReportSearchModel: CustomReportConfigSearchModel): FUINavigationLoader {
    return new NavigationLoader(httpClient, customReportSearchModel);
}

export function initApp(configService: ConfigurationService) {
    return () => {
        return new Promise<void>((resolve) => {
            configService.getString(Local.WHOAMI).then(iAm => {
                WhoAmI.IAM = iAm;
                console.log("I am " + WhoAmI.IAM);
                resolve();
            });
        });
    }
}

/**
 * The Core Module is responsible for importing and providing foundation modules and services required by the
 * application.
 * The Core Module should not declare any components of its own.
 *
 * The Core Module should only be imported once (in the app.module)
 *
 * See https://angular.io/guide/ngmodule#the-core-module
 */
@NgModule({
    imports: [
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: translateLoaderFactory,
                deps: [I18nService, HttpClient]
            }
        }),
        AuthModule.forRoot(),
        FedeltaHttpModule.forRoot(),
        ConfigurationModule.forRoot({
            application: "wbo",
            localConfigFile: "assets/appconfig.json",
            contextNames: ["userId", "role", "application"]
        }),
        ValidationModule.forRoot({
            application: "wbo"
        }),
        ErrorModule.forRoot(),
        I18nModule.forRoot(),
        ResourceModule.forRoot({
            nameSpace: "bo",
            useAsync: true
        }),
        UiModule.forRoot({
            providedLoader: {
                provide: FUINavigationLoader,
                useFactory: navigationLoaderFactory,
                deps: [HttpClient, CustomReportConfigSearchModel]
            }
        }),
        ZXingScannerModule
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        AuthGuardService,
        CanDeactivateEditPage,
        CanDeactivateStackedModal,
        ErrorHandlerService,
        VersionService,
        DataClientVersion,
        SessionService,
        WBOVersion,
        CustomerSupportMessagingService,
        AnalyticsService,
        ImageService,
        UserProfileSummaryEntityModel,
        {
            provide: APP_INITIALIZER,
            useFactory: initApp,
            multi: true,
            deps: [ConfigurationService]
        },
        CustomReportConfigSearchModel,
        DragDropConfig,
        {
            provide: DragDropService,
            useFactory: dragDropServiceFactory
        },
        {
            provide: DragDropSortableService,
            useFactory: dragDropSortableServiceFactory,
            deps: [DragDropConfig]
        }
    ],
    exports: [
        TranslateModule
    ]
})
export class CoreModule {
    constructor(@Optional() @SkipSelf() parentModule: CoreModule, private versionService: VersionService, private alertService: FUIAlertService,
                private errorHandlerService: ErrorHandlerService) {
        if (parentModule) {
            throw new Error("CoreModule is already loaded. Import it in the AppModule only");
        }
        versionService.versionMismatch$.pipe(first()).subscribe(() => {
            this.alertService.showInformation("wbo.search.prompt.version.heading", "wbo.search.prompt.version.message").subscribe(() => {
                location.reload();
            });
        });
    }
}
